/**
 * Eigene Menü-Symbole von Modulen (ADR-010): Knoten wie in `ICONS`, aber aus dem Modulpaket.
 * Die Shell vertraut ihnen nicht, auch wenn Register und Datenbank sie schon geprüft haben:
 * `knotenBereinigen` lässt nur die Positivliste zu (dieselbe wie scripts/modul-symbol.mjs und
 * platform.modul_symbol_pruefen) und liefert sonst `null` — dann greift der Name bzw. der
 * Platzhalter. Gezeichnet wird nur über React-Elemente, nie als HTML-Text.
 */
const ZAHL = /^-?\d+(\.\d+)?$/;
const ZAHLEN = /^[0-9eE.,\t\r\n\f +-]{1,2000}$/;
const WERTE = {
    d: /^[MmLlHhVvCcSsQqTtAaZz0-9eE.,\t\r\n\f +-]{1,2000}$/,
    cx: ZAHL,
    cy: ZAHL,
    r: ZAHL,
    rx: ZAHL,
    ry: ZAHL,
    x: ZAHL,
    y: ZAHL,
    width: ZAHL,
    height: ZAHL,
    x1: ZAHL,
    y1: ZAHL,
    x2: ZAHL,
    y2: ZAHL,
    points: ZAHLEN,
    transform: /^([ \t\r\n\f]*(translate|rotate|scale|skewX|skewY|matrix)\([0-9eE.,\t\r\n\f +-]{1,200}\))+[ \t\r\n\f]*$/,
    'fill-rule': /^(nonzero|evenodd)$/,
    'clip-rule': /^(nonzero|evenodd)$/,
    'stroke-linecap': /^(butt|round|square)$/,
    'stroke-linejoin': /^(miter|round|bevel)$/,
};
const ELEMENTE = new Set(['path', 'circle', 'rect', 'line', 'polyline', 'polygon', 'ellipse']);
const MAX_KNOTEN = 64;
const MAX_ZEICHEN = 6144;
/** React kennt SVG-Attribute nur in camelCase. */
const REACT_NAME = {
    'fill-rule': 'fillRule',
    'clip-rule': 'clipRule',
    'stroke-linecap': 'strokeLinecap',
    'stroke-linejoin': 'strokeLinejoin',
};
const gemerkt = new WeakMap();
/**
 * Prüft unbekannte Daten (Manifest) und gibt zeichenbare Knoten zurück, sonst `null`.
 * Das Ergebnis wird je Eingabeobjekt gemerkt — Icons rendern oft, das Manifest ändert sich nicht.
 */
export function knotenBereinigen(roh) {
    if (roh === null || typeof roh !== 'object')
        return null;
    if (gemerkt.has(roh))
        return gemerkt.get(roh) ?? null;
    const ergebnis = pruefen(roh);
    gemerkt.set(roh, ergebnis);
    return ergebnis;
}
function pruefen(roh) {
    if (!Array.isArray(roh) || roh.length === 0 || roh.length > MAX_KNOTEN)
        return null;
    try {
        if (JSON.stringify(roh).length > MAX_ZEICHEN)
            return null;
    }
    catch {
        return null;
    }
    const knoten = [];
    for (const eintrag of roh) {
        if (!Array.isArray(eintrag) || eintrag.length !== 2)
            return null;
        const [tag, attrs] = eintrag;
        if (typeof tag !== 'string' || !ELEMENTE.has(tag))
            return null;
        if (!attrs || typeof attrs !== 'object' || Array.isArray(attrs))
            return null;
        const sauber = {};
        for (const [name, wert] of Object.entries(attrs)) {
            const muster = Object.prototype.hasOwnProperty.call(WERTE, name) ? WERTE[name] : undefined;
            if (!muster || typeof wert !== 'string' || !muster.test(wert))
                return null;
            sauber[REACT_NAME[name] ?? name] = wert;
        }
        knoten.push([tag, sauber]);
    }
    return knoten;
}
